import { OrganizationUserRolesEnum } from "@propertelligent/client-api";
import { UserLoginData } from "../components/common/charts/Enums";

/**
 * Mirrors the backend's Propertelligent.Service/Permissions/DashboardPanelAccessPolicy.cs.
 * Deliberately NOT the same map as the general per-role module-access permissions (usePermissions) --
 * e.g. LeasingManager has read access to ServiceRequest elsewhere in the app, but must see ZERO
 * Maintenance dashboard data. Keep both sides of this policy in sync when either changes.
 */
export enum DashboardPanelEnum {
  Leasing = "Leasing",
  Maintenance = "Maintenance",
  Payments = "Payments",
}

const DASHBOARD_PANEL_ACCESS: Record<DashboardPanelEnum, OrganizationUserRolesEnum[]> = {
  [DashboardPanelEnum.Leasing]: [
    OrganizationUserRolesEnum.Admin,
    OrganizationUserRolesEnum.PropertyManager,
    OrganizationUserRolesEnum.LeasingManager,
  ],
  [DashboardPanelEnum.Maintenance]: [
    OrganizationUserRolesEnum.Admin,
    OrganizationUserRolesEnum.PropertyManager,
    OrganizationUserRolesEnum.MaintenanceManager,
  ],
  [DashboardPanelEnum.Payments]: [
    OrganizationUserRolesEnum.Admin,
    OrganizationUserRolesEnum.PropertyManager,
    OrganizationUserRolesEnum.Accountant,
  ],
};

/**
 * Returns whether the given organization-user role may see/fetch data for the given dashboard panel.
 * A null/undefined role (e.g. missing claim, non-organization caller) is denied.
 */
export const canAccessDashboardPanel = (
  role: OrganizationUserRolesEnum | null | undefined,
  panel: DashboardPanelEnum
): boolean => {
  if (role === null || role === undefined) {
    return false;
  }
  return DASHBOARD_PANEL_ACCESS[panel]?.includes(role) ?? false;
};

/**
 * Reads the logged-in organization user's role from sessionStorage (set at login) and parses it into
 * the OrganizationUserRolesEnum used by the access policy. Returns null when missing/unparsable
 * (e.g. non-organization callers such as Tenant/Contractor never have this value set).
 */
export const getCurrentOrganizationUserRole = (): OrganizationUserRolesEnum | null => {
  const stored = sessionStorage.getItem(UserLoginData.organization_user_role);
  if (!stored) {
    return null;
  }
  const parsed = Number(stored);
  const validRoleIds = Object.values(OrganizationUserRolesEnum).filter(
    (value): value is number => typeof value === "number"
  );
  return Number.isFinite(parsed) && validRoleIds.includes(parsed)
    ? (parsed as OrganizationUserRolesEnum)
    : null;
};
